import type { MouseEventHandler } from 'react'
import type { IntlShape } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import { PENDING_TIMEOUT_MS, showTripPlannerSnackbar } from './TripPlannerSnackbarHost'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// A determinate-looking arc over a full track, per the design. The repo's LineSpinnerIcon is
// a ring of dots, which reads differently, so this draws the arc directly.
const SPINNER_SIZE = 20
const SPINNER_STROKE = 2
const SPINNER_CENTRE = SPINNER_SIZE / 2
const SPINNER_RADIUS = (SPINNER_SIZE - SPINNER_STROKE) / 2
const SPINNER_CIRCUMFERENCE = 2 * Math.PI * SPINNER_RADIUS
const SPINNER_ARC_LENGTH = SPINNER_CIRCUMFERENCE * 0.28

const SpinnerSvg = styled.svg`
  flex-shrink: 0;
  animation: ${spin} 800ms linear infinite;
`

const SpinnerTrack = styled.circle`
  stroke: ${props => props.theme.palette.neutral.default.five};
`

const SpinnerArc = styled.circle`
  stroke: ${props => props.theme.palette.neutral.default.one};
`

function TripActionSpinner() {
  return (
    <SpinnerSvg
      width={SPINNER_SIZE}
      height={SPINNER_SIZE}
      viewBox={`0 0 ${SPINNER_SIZE} ${SPINNER_SIZE}`}
      fill="none"
      aria-hidden="true"
    >
      <SpinnerTrack
        cx={SPINNER_CENTRE}
        cy={SPINNER_CENTRE}
        r={SPINNER_RADIUS}
        strokeWidth={SPINNER_STROKE}
      />
      <SpinnerArc
        cx={SPINNER_CENTRE}
        cy={SPINNER_CENTRE}
        r={SPINNER_RADIUS}
        strokeWidth={SPINNER_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${SPINNER_ARC_LENGTH} ${SPINNER_CIRCUMFERENCE - SPINNER_ARC_LENGTH}`}
        transform={`rotate(-90 ${SPINNER_CENTRE} ${SPINNER_CENTRE})`}
      />
    </SpinnerSvg>
  )
}

export type TripActionOperation = 'add' | 'update' | 'delete'

// Same precedence useItineraryUpdates applies within a batch, so it reads consistently
// when extended across a turn.
const OPERATION_RANK: Record<TripActionOperation, number> = { delete: 3, add: 2, update: 1 }

// Module-level to match the host's singleton API, and because most producers render outside
// the trip planner provider (the offer-page assistant mounts them too).
let turnOperation: TripActionOperation | undefined
let turnExpiryTimeout: number | undefined

/**
 * The latch outlives its toast only until the host would have retired it. Trip chat clears
 * it far sooner at turn end, but producers on other surfaces have no turn to end, and a
 * latch left set there suppresses the next confirmation wherever it lands.
 */
function markTurnOperation(operation: TripActionOperation): void {
  turnOperation = operation
  window.clearTimeout(turnExpiryTimeout)
  turnExpiryTimeout = window.setTimeout(clearTripActionTurn, PENDING_TIMEOUT_MS)
}

/**
 * False when a higher-ranked operation has already been shown this turn, so a side-effect
 * update cannot replace the confirmation for what the traveller actually asked for.
 */
export function shouldConfirmTripAction(operation: TripActionOperation): boolean {
  return turnOperation === undefined || OPERATION_RANK[operation] >= OPERATION_RANK[turnOperation]
}

/** Ends the turn's latch. Called at stream end, on chat switch, and by the manual paths. */
export function clearTripActionTurn(): void {
  turnOperation = undefined
  window.clearTimeout(turnExpiryTimeout)
  turnExpiryTimeout = undefined
}

function getPendingHeading(intl: IntlShape, operation: TripActionOperation): string {
  switch (operation) {
    case 'add':
      return intl.formatMessage({
        id: 'o1FhOaeNDDoe5Qhkml_Z',
        defaultMessage: 'Adding to trip',
        description: 'Snackbar heading shown while a place is being added to the traveller itinerary and the request is still in flight',
      })
    case 'update':
      return intl.formatMessage({
        id: 'RiQa9bclQthgjurLMjed',
        defaultMessage: 'Updating',
        description: 'Snackbar heading shown while a change to an itinerary item is still in flight in the trip planner',
      })
    case 'delete':
      return intl.formatMessage({
        id: 'J2nIOAWpNUCJMFpF93U3',
        defaultMessage: 'Deleting',
        description: 'Snackbar heading shown while an itinerary item is being removed and the request is still in flight',
      })
  }
}

function getConfirmedHeading(intl: IntlShape, operation: TripActionOperation): string {
  switch (operation) {
    case 'add':
      return intl.formatMessage({
        id: 'EkhV7UlntGcasG6J6OgV',
        defaultMessage: 'Added to trip',
        description: 'Snackbar heading confirming that a place has been added to the traveller itinerary successfully',
      })
    case 'update':
      return intl.formatMessage({
        id: 'e5M5LLenGqAOj-vksOzi',
        defaultMessage: 'Updated',
        description: 'Snackbar heading confirming that a change to an itinerary item completed successfully',
      })
    case 'delete':
      return intl.formatMessage({
        id: 'iKi4oqYvI6cHoFtY34qI',
        defaultMessage: 'Deleted',
        description: 'Snackbar heading confirming that an itinerary item was removed from the trip successfully',
      })
  }
}

/** Raised when the traveller commits; the confirmation replaces it when the work lands. */
export function showTripActionPendingSnackbar(
  intl: IntlShape,
  operation: TripActionOperation,
  name: string,
) {
  markTurnOperation(operation)
  showTripPlannerSnackbar({
    description: name,
    kind: 'neutral',
    heading: getPendingHeading(intl, operation),
    icon: <TripActionSpinner />,
    pending: true,
  })
}

/** Deletions pass no `onView` — nothing left to look at. */
export function showTripActionConfirmedSnackbar(
  intl: IntlShape,
  operation: TripActionOperation,
  name: string,
  onView?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
) {
  markTurnOperation(operation)
  showTripPlannerSnackbar({
    description: name,
    kind: 'success',
    heading: getConfirmedHeading(intl, operation),
    icon: <SolidCheckCircleIcon colour="success" size="S" />,
    action: onView && {
      label: intl.formatMessage({
        id: 'kqS_gxdAaK9Wl95_SlV1',
        defaultMessage: 'View',
        description: 'Snackbar action that opens the List view and scrolls to the item that was just changed in the trip',
      }),
      onAction: onView,
      testId: 'trip-action-view',
    },
  })
}

/** Replaces a pending toast rather than leaving it spinning to its timeout. */
export function showTripActionFailedSnackbar(
  intl: IntlShape,
  message: string,
  kind: 'critical' | 'warning' = 'critical',
) {
  clearTripActionTurn()
  showTripPlannerSnackbar({ description: message, kind })
}
